export type CardType = "visa" | "mastercard" | "amex" | "discover" | "diners-club" | "jcb" | "unionpay";

export interface CardDefinition {
  type: CardType;
  icon: string;
  prefixPatterns: RegExp[];
  validLengths: number[];
  cvvLength: number;
}

const CARD_DEFINITIONS: CardDefinition[] = [
  {
    type: "visa",
    icon: "visa",
    prefixPatterns: [/^4/],
    validLengths: [13, 16, 19],
    cvvLength: 3
  },
  {
    type: "mastercard",
    icon: "mastercard",
    prefixPatterns: [/^5[1-5]/, /^2(2[2-9][1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720)/],
    validLengths: [16],
    cvvLength: 3
  },
  {
    type: "amex",
    icon: "amex",
    prefixPatterns: [/^3[47]/],
    validLengths: [15],
    cvvLength: 4
  },
  {
    type: "discover",
    icon: "discover",
    prefixPatterns: [/^6011/, /^65/, /^64[4-9]/],
    validLengths: [16, 19],
    cvvLength: 3
  },
  {
    type: "diners-club",
    icon: "diners-club",
    prefixPatterns: [/^30[0-5]/, /^36/, /^38/],
    validLengths: [14],
    cvvLength: 3
  },
  {
    type: "jcb",
    icon: "jcb",
    prefixPatterns: [/^35(2[89]|[3-8]\d)/],
    validLengths: [16, 17, 18, 19],
    cvvLength: 3
  },
  {
    type: "unionpay",
    icon: "unionpay",
    prefixPatterns: [/^62/],
    validLengths: [16, 17, 18, 19],
    cvvLength: 3
  }
];

export class CreditCardHelpers {
  static detectCardType(cardNumber: string): CardDefinition | null {
    const digits = cardNumber.replace(/\D/g, "");

    if (!digits) {
      return null;
    }

    for (const card of CARD_DEFINITIONS) {
      if (card.prefixPatterns.some(pattern => pattern.test(digits))) {
        return card;
      }
    }

    return null;
  }

  /**
   * Amex uses 4-6-5 grouping; all others use standard 4-digit grouping.
   */
  static formatCardNumber(cardNumber: string): string {
    const digits = cardNumber.replace(/\D/g, "");

    if (!digits) {
      return "";
    }

    const card = CreditCardHelpers.detectCardType(digits);

    if (card?.type === "amex") {
      const parts = [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)];
      return parts.filter(p => p).join(" ");
    }

    const parts: string[] = [];
    for (let i = 0; i < digits.length; i += 4) {
      parts.push(digits.slice(i, i + 4));
    }
    return parts.join(" ");
  }

  static getMaxDigits(card: CardDefinition | null): number {
    if (!card) {
      return 19;
    }
    return Math.max(...card.validLengths);
  }

  static isValidLength(cardNumber: string, card: CardDefinition | null): boolean {
    const digits = cardNumber.replace(/\D/g, "");

    if (!card) {
      return digits.length >= 13 && digits.length <= 19;
    }

    return card.validLengths.includes(digits.length);
  }

  static hasReachedMinLength(cardNumber: string, card: CardDefinition | null): boolean {
    const digits = cardNumber.replace(/\D/g, "");
    const minLength = card ? Math.min(...card.validLengths) : 13;
    return digits.length >= minLength;
  }

  /**
   * Luhn algorithm for card number checksum validation.
   */
  static passesLuhnCheck(cardNumber: string): boolean {
    const digits = cardNumber.replace(/\D/g, "");

    if (!digits || digits.length < 13) {
      return false;
    }

    let sum = 0;
    let shouldDouble = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = parseInt(digits[i], 10);

      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
  }
}
